import { DEFAULT_LOCALE, translate } from '../i18n';
import { renderSpinner } from './spinner';
import KatalMetricsPublisher from '@amzn/katal-metrics/lib/KatalMetricsPublisher';
import { getSupportedLanguages, LanguageInfo } from '@amzn/brand-context-service-type-script-client';
import '../styles/language-picker.scss';
import { formatMetricString, metricsPublisher } from '../util/metrics';
import { renderError } from 'src/components/error';
import { logError } from 'src/util/logger';

interface Locale {
  code: string;
  countryName: string;
  nativeName: string;
}

let languagePickerPublisher: KatalMetricsPublisher;

let languageInfoList: LanguageInfo[];

const selectedLocale = (document.documentElement.lang || navigator.language || DEFAULT_LOCALE).replace('-', '_');

export const getLanguageInfoList = async () => {
  if (!languageInfoList) {
    languageInfoList = await getSupportedLanguages();
  }
  return languageInfoList;
};

const createLocaleContainer = (locale: Locale): HTMLAnchorElement => {
  const { code, countryName, nativeName } = locale;
  const isSelectedLocale = selectedLocale === code;

  const url = new URL(window.location.href);
  url.searchParams.set('mons_sel_locale', code);
  url.searchParams.set('languageSwitched', '1');

  const localeContainer = document.createElement('a');
  localeContainer.href = url.href;

  const formattedCode = code.slice(3);

  localeContainer.className =
      `brand-registry-language-picker-locale-container${isSelectedLocale 
        ? ' brand-registry-language-picker-locale-container-selected' 
        : ''}`;
  localeContainer.id = `brand-registry-language-picker-${formattedCode}-container`;
  localeContainer.ariaLabel = `brand-registry-language-picker-${formattedCode}-locale-container`;

  const header = document.createElement('span');
  header.className = 'brand-registry-language-picker-locale-header';
  header.textContent = nativeName;

  const headerDiv = document.createElement('div');
  headerDiv.className = 'tw-br-trim-flex tw-br-trim-justify-between';
  headerDiv.appendChild(header);

  if (isSelectedLocale) {
    const checkmarkIcon = document.createElement('img');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    checkmarkIcon.src = require('../images/check.svg').default;
    headerDiv.appendChild(checkmarkIcon);
  }

  localeContainer.appendChild(headerDiv);

  const bodyDiv = document.createElement('div');
  const localeText = document.createElement('span');
  bodyDiv.className = 'brand-registry-language-picker-secondary-text';
  localeText.textContent =
      translate('br-header-language-picker-locale-text', { nativeName: countryName, localeCode: formattedCode });
  bodyDiv.appendChild(localeText);

  localeContainer.appendChild(bodyDiv);

  const metricCodeString = formatMetricString(code);

  localeContainer.onclick = () => {
    if (isSelectedLocale) return;
    languagePickerPublisher.publishCounter('LanguageChanged', 1);
    languagePickerPublisher.publishString('LanguageChanged', metricCodeString);
    window.location.href = url.href;
  };

  return localeContainer;
};

export const renderLanguagePicker = (): HTMLDivElement => {
  languagePickerPublisher = metricsPublisher.newChildActionPublisherForMethod('LanguagePicker');

  const selectedLanguage = selectedLocale.slice(0, 2);
  const GAP_SIZE = 8;
  const ROW_HEIGHT = 58;
  const MIN_ROWS = 1;
  const MAX_ROWS = 8;

  const languagePicker = document.createElement('div');
  const languagePickerSelector = 'brand-registry-language-picker-container';
  languagePicker.className = languagePickerSelector;
  languagePicker.id = languagePickerSelector;

  const languagePickerLink = document.createElement('a');
  languagePickerLink.href = '/';
  const linkSelector = 'brand-registry-language-picker-link';
  languagePickerLink.className = linkSelector;
  languagePickerLink.id = linkSelector;
  languagePickerLink.ariaLabel = 'brand-registry-language-picker';

  const dropdownClass = 'brand-registry-header-dropdown-icon';

  const languageHeader = document.createElement('span');
  languageHeader.textContent = selectedLanguage.toLocaleUpperCase(selectedLanguage);
  const dropdownIcon = document.createElement('div');
  dropdownIcon.className = `${dropdownClass}-closed`;
  languagePickerLink.appendChild(languageHeader);
  languagePickerLink.appendChild(dropdownIcon);

  languagePicker.appendChild(languagePickerLink);

  const popoverContainerSelector = 'brand-registry-language-picker-popover-container';
  const popoverDiv = document.createElement('div');
  popoverDiv.className = popoverContainerSelector;
  popoverDiv.id = popoverContainerSelector;

  const headerDiv = document.createElement('div');
  headerDiv.className = 'brand-registry-language-picker-header-container';

  const header = document.createElement('h4');

  header.textContent = translate('br-header-language-picker-title');

  const searchInput = document.createElement('input');
  searchInput.className = 'brand-registry-language-picker-search';
  searchInput.id = 'brand-registry-language-picker-search';
  searchInput.name = 'brand-registry-language-picker-search';
  searchInput.ariaLabel = 'brand-registry-language-picker-search-input';

  const searchIcon = document.createElement('img');
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  searchIcon.src = require('../images/search.svg').default;

  headerDiv.appendChild(header);

  const searchDiv = document.createElement('div');
  searchDiv.className = 'tw-br-trim-relative tw-br-trim-block';

  searchDiv.appendChild(searchInput);
  searchDiv.appendChild(searchIcon);

  headerDiv.appendChild(searchDiv);

  popoverDiv.appendChild(headerDiv);

  const localeContainerSelector = 'brand-registry-language-picker-available-locales-container';

  let supportedLanguagesHaveBeenCreated = false;

  const createFilteredLocaleContainers = (filteredLocales: LanguageInfo[]) => {
    const localeContainer = document.createElement('div');
    localeContainer.className = localeContainerSelector;
    localeContainer.id = localeContainerSelector;

    if (!filteredLocales.length) {
      const noResultsText = document.createElement('div');
      noResultsText.className = 'brand-registry-language-picker-no-results';
      noResultsText.textContent = translate('br-header-language-picker-no-results');
      localeContainer.appendChild(noResultsText);

      return localeContainer;
    }

    const rows = Math.min(Math.max(Math.ceil(filteredLocales.length / 2), MIN_ROWS), MAX_ROWS);
    const height = ROW_HEIGHT * rows + GAP_SIZE * (rows - 1);

    localeContainer.style.maxHeight = `${height}px`;

    const locales = filteredLocales.map(createLocaleContainer);

    locales.forEach((locale) => {
      localeContainer.appendChild(locale);
    });

    return localeContainer;
  };

  const createLocaleContainers = async () => {
    if (supportedLanguagesHaveBeenCreated) return;

    const spinnerDiv = document.createElement('div');
    spinnerDiv.classList.add('tw-br-trim-mt-8', 'tw-br-trim-mb-4');
    const spinner = renderSpinner();
    spinnerDiv.append(spinner);
    popoverDiv.append(spinnerDiv);

    try {
      const supportedLanguages = await getLanguageInfoList();

      const localeContainer = createFilteredLocaleContainers(supportedLanguages);

      supportedLanguagesHaveBeenCreated = true;

      popoverDiv.removeChild(spinnerDiv);
      popoverDiv.append(localeContainer);
    } catch (error) {
      languagePickerPublisher.publishCounterMonitor('LanguagePickerFailure', 1);
      supportedLanguagesHaveBeenCreated = true;
      popoverDiv.removeChild(spinnerDiv);

      logError('An error occurred loading the language picker', error);

      const errorContainer = document.createElement('div');
      errorContainer.classList.add('tw-br-trim-min-w-[375px]', 'tw-br-trim-whitespace-normal');
      errorContainer.append(renderError());

      popoverDiv.append(errorContainer);
    }
  };

  searchInput.addEventListener('input', async (e) => {
    const localeContainer = document.getElementById(localeContainerSelector);

    if (!e.target || !localeContainer) return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - for some reason it doesn't think value will exist
    const searchTextLowerCase: string = e.target.value.toLowerCase();

    const supportedLanguages = await getLanguageInfoList();

    const filteredLocales = supportedLanguages.filter((locale) => {
      const matchesNativeName = locale.nativeName.toLowerCase().includes(searchTextLowerCase);
      const matchesCountryName = locale.countryName.toLowerCase().includes(searchTextLowerCase);
      const matchesCode = locale.code.toLowerCase().includes(searchTextLowerCase);

      return matchesCountryName || matchesNativeName || matchesCode;
    });

    const filteredLocaleContainer = createFilteredLocaleContainers(filteredLocales);

    popoverDiv.removeChild(localeContainer);
    popoverDiv.append(filteredLocaleContainer);
  });

  const resetFilteredLanguages = async () => {
    const localeContainer = document.getElementById(localeContainerSelector);
    if (!localeContainer) return;
    searchInput.value = '';
    const resetLocaleContainer = createFilteredLocaleContainers(await getLanguageInfoList());
    popoverDiv.removeChild(localeContainer);
    popoverDiv.append(resetLocaleContainer);
  };

  languagePickerLink.onclick = async (e) => {
    e.preventDefault();
    if (dropdownIcon.className === `${dropdownClass}-closed`) {
      dropdownIcon.className = `${dropdownClass}-open`;
      popoverDiv.style.visibility = 'visible';
      await createLocaleContainers();
      languagePickerPublisher.publishCounter('LanguagePickerOpened', 1);
    } else {
      dropdownIcon.className = `${dropdownClass}-closed`;
      popoverDiv.style.visibility = 'hidden';
      // clear out search and filtered locales if typed in
      if (searchInput.value.length) {
        await resetFilteredLanguages();
      }
      languagePickerPublisher.publishCounter('LanguagePickerClosed', 1);
    }
  };

  // when clicked out of, close the language picker
  document.addEventListener('click', async (e) => {
    if (!e.composedPath().includes(languagePicker) && popoverDiv.style.visibility === 'visible') {
      dropdownIcon.className = `${dropdownClass}-closed`;
      popoverDiv.style.visibility = 'hidden';
      // clear out search and filtered locales if typed in
      if (searchInput.value.length) {
        await resetFilteredLanguages();
      }
      languagePickerPublisher.publishCounter('LanguagePickerClosedClickedOutside', 1);
    }
  });

  languagePicker.appendChild(popoverDiv);

  return languagePicker;
};
